import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  IconButton,
  Autocomplete
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Visibility,
  VisibilityOff,
  Person as PersonIcon,
  Work as WorkIcon,
  AdminPanelSettings as AdminIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { makeApiCall } from '../../config/api';
import SalesSignatureSection from './SalesSignatureSection';

// TKT-538: this is an admin form about ANOTHER user, but it has a username and
// a password box, so browsers and password managers treat it as a login form
// and fill in a saved login (Naomi saw Calvin Kent's username change to Jamie,
// Julio, Carlos). Opt every field out of autofill. "new-password" is the value
// Chrome actually honours on password boxes; the data-* attributes cover
// LastPass, 1Password and Dashlane.
const NO_AUTOFILL = {
  autoComplete: 'off',
  'data-lpignore': 'true',
  'data-1p-ignore': 'true',
  'data-form-type': 'other',
};
const NEW_PASSWORD = { ...NO_AUTOFILL, autoComplete: 'new-password' };

const UserForm = ({ user, onSuccess, onCancel, lookups }) => {
  const { user: currentUser } = useAuth();
  const { t } = useTranslation();
  // TKT-482: only a Super Admin may change a Super Admin's account. The
  // server refuses it too; this keeps an admin from typing changes that
  // can't be saved.
  const superAdminLocked = !!user && user.user_type === 'superadmin'
    && currentUser?.user_type !== 'superadmin';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Form state - focused purely on user/system access
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    user_type: 'employee',
    privileges: '',
    is_active: true,
    password_expiry: '',
    // Role flags (TKT-275: previously not exposed in this form at all,
    // so there was no way to actually configure a Manager - the backend
    // supported the flag but nothing in the UI ever sent it)
    is_manager: false,
    is_sales_person: false,
    // TKT-279: Crew Mode has no way to know which truck a laborer (not the
    // formal driver/passenger) belongs to, so the manual truck picker
    // showed every active truck fleet-wide. Office-assigned truck gives
    // Crew Mode a source of truth for that case.
    assigned_truck_id: '',
    // TKT-461: a subcontractor's trucks (the office picks several)
    truck_ids: [],
    // Optional employee linking
    employee_id: null
  });

  // Available employees for linking
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [availableTrucks, setAvailableTrucks] = useState([]);

  // Initialize form with user data if editing
  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        password: '',
        confirmPassword: '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        // Map legacy tiers (normal/crew/inactive) onto Employee while keeping
        // every current assignable role selectable.
        user_type: ['superadmin', 'admin', 'sales', 'crew_manager', 'subcontractor'].includes(user.user_type)
          ? user.user_type
          : 'employee',
        privileges: user.privileges || '',
        is_active: user.is_active !== undefined ? user.is_active : true,
        password_expiry: user.password_expiry ? user.password_expiry.split('T')[0] : '',
        is_manager: user.is_manager || false,
        is_sales_person: user.is_sales_person || false,
        assigned_truck_id: user.assigned_truck_id || '',
        truck_ids: [],
        employee_id: user.employee_id || null
      });
    }
  }, [user]);

  // TKT-461: the user's trucks are the ones listing them as additional crew,
  // plus their assigned truck (so switching an employee to Subcontractor
  // keeps it).
  useEffect(() => {
    if (!user?.id || availableTrucks.length === 0) return;
    const ids = availableTrucks
      .filter((truck) => truck.id === user.assigned_truck_id
        || (truck.additional_crew || []).some((member) => member.user_id === user.id))
      .map((truck) => truck.id);
    setFormData((prev) => ({ ...prev, truck_ids: ids }));
  }, [user, availableTrucks]);

  // Fetch available employees for linking
  useEffect(() => {
    fetchAvailableEmployees();
    fetchAvailableTrucks();
  }, []);

  const fetchAvailableTrucks = async () => {
    try {
      const response = await makeApiCall('/api/routes/trucks');
      if (response.ok) {
        const data = await response.json();
        setAvailableTrucks((data.trucks || []).filter((t) => t.is_active !== false));
      }
    } catch (err) {
      console.error('Error fetching trucks:', err);
    }
  };

  const fetchAvailableEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const response = await makeApiCall('api/employees?is_active=true&has_user=false&limit=1000');
      if (response.ok) {
        const data = await response.json();
        setAvailableEmployees(data.employees || []);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Handle form field changes
  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (error) setError('');
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validation
    if (!formData.username || !formData.email) {
      setError('Username and email are required');
      return;
    }

    if (!user && !formData.password) {
      setError('Password is required for new users');
      return;
    }

    if (formData.password && formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (superAdminLocked) {
      setError(t('userForm.superAdminLocked'));
      return;
    }

    try {
      setLoading(true);
      
      // Prepare data for submission
      const submitData = { ...formData };
      delete submitData.confirmPassword; // Remove confirm password field
      // TKT-461: a subcontractor's trucks all go in the Trucks list, so the
      // single assigned truck is cleared. Other users keep their trucks as is.
      if (submitData.user_type === 'subcontractor') {
        submitData.assigned_truck_id = null;
      } else {
        delete submitData.truck_ids;
      }

      // TKT-252: when editing an existing user, NEVER resubmit the
      // username from the form. The field is already rendered disabled,
      // but if anything ever desyncs (cross-row state bleed, stale
      // dialog), submitting an unintended username can collide with
      // the unique constraint and brick the user account. Backend
      // COALESCEs missing username with the existing value.
      if (user) {
        delete submitData.username;
      }
      
      const endpoint = user 
        ? `api/users/${user.id}`
        : 'api/users';
      
      const method = user ? 'PUT' : 'POST';

      const response = await makeApiCall(endpoint, {
        method,
        body: JSON.stringify(submitData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save user');
      }

      onSuccess(data.message || 'User saved successfully');

    } catch (error) {
      console.error('Save user error:', error);
      setError(error.message || 'Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlinkEmployee = async () => {
    if (!window.confirm('Are you sure you want to unlink this user from their employee record? This will remove the connection but not delete either record.')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      await makeApiCall(`/api/users/${user.id}/unlink-employee`, {
        method: 'POST',
      });

      // Refresh the user data or notify parent
      onSuccess && onSuccess();
    } catch (error) {
      console.error('Unlink employee error:', error);
      setError(error.message || 'Failed to unlink employee');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      autoComplete="off"
      data-lpignore="true"
      data-1p-ignore="true"
      data-form-type="other"
      sx={{ mt: 2 }}
    >
      {superAdminLocked && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('userForm.superAdminLocked')}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* User Information Section */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon color="primary" />
            <Typography variant="h6">User Information</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required
                // TKT-538: when editing, no name attribute (so it doesn't look
                // like a login username box) and always show the stored
                // username, never whatever a browser typed into the box.
                name={user ? undefined : 'username'}
                label="Username"
                value={user ? (user.username || '') : formData.username}
                onChange={handleChange}
                disabled={!!user} // Username can't be changed after creation
                inputProps={{ ...NO_AUTOFILL, readOnly: !!user }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required
                name="email"
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={handleChange}
                inputProps={NO_AUTOFILL}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required={!user}
                name="password"
                label={user ? "New Password (leave blank to keep current)" : "Password"}
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                inputProps={NEW_PASSWORD}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required={!!formData.password}
                name="confirmPassword"
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                disabled={!formData.password}
                inputProps={NEW_PASSWORD}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="first_name"
                label="First Name"
                value={formData.first_name}
                onChange={handleChange}
                inputProps={NO_AUTOFILL}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="last_name"
                label="Last Name"
                value={formData.last_name}
                onChange={handleChange}
                inputProps={NO_AUTOFILL}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>User Type</InputLabel>
                <Select
                  name="user_type"
                  value={formData.user_type}
                  label="User Type"
                  onChange={handleChange}
                >
                  {/* TKT-221: 5-tier role model. "Inactive" is the Active
                      toggle below (login is blocked when inactive), not a
                      user_type. Super Admin can only be assigned by a Super
                      Admin. */}
                  <MenuItem value="employee">{t('roles.employee')}</MenuItem>
                  <MenuItem value="crew_manager">{t('roles.crewManager')}</MenuItem>
                  <MenuItem value="sales">{t('roles.sales')}</MenuItem>
                  <MenuItem value="admin">{t('roles.admin')}</MenuItem>
                  {currentUser?.user_type === 'superadmin' && (
                    <MenuItem value="superadmin">{t('roles.superadmin')}</MenuItem>
                  )}
                  {/* TKT-461: Crew Mode only, for the trucks picked below. */}
                  <MenuItem value="subcontractor">{t('roles.subcontractor')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="password_expiry"
                label="Password Expiry Date"
                type="date"
                value={formData.password_expiry}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={handleChange}
                    name="is_active"
                    inputProps={{ 'aria-label': 'Active User' }}
                  />
                }
                label="Active User"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_manager}
                    onChange={handleChange}
                    name="is_manager"
                    inputProps={{ 'aria-label': 'Manager' }}
                  />
                }
                label="Manager"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6, mt: -0.5 }}>
                Makes this user selectable in Manager Name dropdowns on Employee Forms.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_sales_person}
                    onChange={handleChange}
                    name="is_sales_person"
                    inputProps={{ 'aria-label': 'Sales Person' }}
                  />
                }
                label="Sales Person"
              />
            </Grid>

            {formData.user_type === 'subcontractor' ? (
              <Grid size={12}>
                <Autocomplete
                  multiple
                  filterSelectedOptions
                  options={availableTrucks}
                  value={availableTrucks.filter((truck) => formData.truck_ids.includes(truck.id))}
                  onChange={(event, trucks) => setFormData((prev) => ({
                    ...prev,
                    truck_ids: trucks.map((truck) => truck.id),
                  }))}
                  getOptionLabel={(truck) => truck.name || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  noOptionsText={t('userForm.subcontractorTrucksNone')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('userForm.subcontractorTrucks')}
                      helperText={t('userForm.subcontractorTrucksHelp')}
                    />
                  )}
                />
              </Grid>
            ) : (
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Assigned Truck (Crew Mode)</InputLabel>
                  <Select
                    name="assigned_truck_id"
                    value={formData.assigned_truck_id}
                    label="Assigned Truck (Crew Mode)"
                    onChange={handleChange}
                    inputProps={{ 'aria-label': 'Assigned Truck (Crew Mode)' }}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {availableTrucks.map((truck) => (
                      <MenuItem key={truck.id} value={truck.id}>{truck.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  For crew (laborers) who aren't the truck's driver or passenger. Scopes their Crew Mode schedule/clock-in to this truck only, instead of letting them pick from every truck.
                </Typography>
              </Grid>
            )}

            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                name="privileges"
                label="Special Privileges (optional)"
                value={formData.privileges}
                onChange={handleChange}
                placeholder="Enter any special privileges or permissions"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {user && user.is_sales_person && !superAdminLocked && (
        <SalesSignatureSection
          userId={user.id}
          canSetDefaultSigner={['admin', 'superadmin'].includes(currentUser?.user_type)}
        />
      )}

      {/* Employee Linking Section */}
      <Accordion defaultExpanded={!user || !user.employee_id} sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WorkIcon color="primary" />
            <Typography variant="h6">Employee Linking</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Link this user account to an employee record to connect system login with HR data, timesheets, and vehicle assignments.
          </Typography>
          
          {user && user.employee_id ? (
            <Alert 
              severity={user.employee_name ? "info" : "warning"} 
              action={
                <Button 
                  size="small" 
                  color="inherit"
                  onClick={handleUnlinkEmployee}
                  disabled={loading || superAdminLocked}
                >
                  Unlink Employee
                </Button>
              }
            >
              {user.employee_name ? (
                <>
                  This user is linked to employee: <strong>{user.employee_name}</strong>
                  <br />
                  <Typography variant="body2" color="text.secondary">
                    Use Employee Management to modify employment details.
                  </Typography>
                </>
              ) : (
                <>
                  <strong>⚠️ Data Integrity Issue:</strong> This user is linked to employee ID {user.employee_id} but the employee record may be missing or inactive.
                  <br />
                  <Typography variant="body2" color="text.secondary">
                    Click "Unlink Employee" to resolve this issue.
                  </Typography>
                </>
              )}
            </Alert>
          ) : (
            <Autocomplete
              fullWidth
              options={availableEmployees}
              getOptionLabel={(option) => option.employee_name || ''}
              value={selectedEmployee}
              onChange={(event, newValue) => {
                setSelectedEmployee(newValue);
                setFormData(prev => ({
                  ...prev,
                  employee_id: newValue ? newValue.id : null
                }));
              }}
              loading={loadingEmployees}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Employee (Optional)"
                  placeholder="Search for an employee to link..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingEmployees ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body1">{option.employee_name}</Typography>
                    {option.department_name && (
                      <Typography variant="caption" color="text.secondary">
                        {option.department_name} {option.job_title && `- ${option.job_title}`}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              noOptionsText="No available employees (all may already have user accounts)"
            />
          )}
        </AccordionDetails>
      </Accordion>

      {/* Legacy alert - keeping for backwards compatibility if needed */}
      {user && user.employee_id && false && (
        <Alert 
          severity={user.employee_name ? "info" : "warning"} 
          sx={{ mt: 2 }}
          action={
            <Button 
              size="small" 
              color="inherit"
              onClick={handleUnlinkEmployee}
              disabled={loading}
            >
              Unlink Employee
            </Button>
          }
        >
          {user.employee_name ? (
            <>
              This user is linked to employee: <strong>{user.employee_name}</strong>
              <br />
              <Typography variant="body2" color="text.secondary">
                Use Employee Management to modify employment details.
              </Typography>
            </>
          ) : (
            <>
              <strong>⚠️ Data Integrity Issue:</strong> This user is linked to employee ID {user.employee_id} but the employee record may be missing or inactive.
              <br />
              <Typography variant="body2" color="text.secondary">
                Click "Unlink Employee" to resolve this issue.
              </Typography>
            </>
          )}
        </Alert>
      )}

      {/* Form Actions */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          onClick={onCancel}
          disabled={loading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={loading || superAdminLocked}
          variant="contained"
          sx={{ minWidth: 120 }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            user ? 'Update User' : 'Create User'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default UserForm; 